/**
 * ModelCapabilitiesPage — /agents/libraries/models
 * The model capability matrix: every model in the bank with its provider, context size, capabilities and findings.
 * Read-only catalogue used for runtime compatibility decisions.
 * Demo example: /tables/data-tables (search, multi-select filters, row click) and /connected-apps (capability tags).
 */
import { AlertTriangle, BrainCircuit, CheckCircle2 } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listModelsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { ListModelsResponse } from '../../generated/api/types.gen.js';
import { ListPage } from '../../components/page/ListPage.js';
import {
  Badge,
  Button,
  Callout,
  Chip,
  DataTable,
  EmptyState,
  MultiSelectDropdown,
  SearchBar,
  Toolbar,
} from '../../components/ui/index.js';
import type { DataTableColumn } from '../../components/ui/index.js';

const CAPABILITY_KEYS = [
  ['supportsReasoning', 'Reasoning'],
  ['supportsTools', 'Tools'],
  ['supportsStructuredOutput', 'Structured'],
  ['supportsVision', 'Vision'],
  ['supportsProviderTools', 'Provider tools'],
  ['supportsWebSearch', 'Web search'],
  ['supportsFiles', 'Files'],
] as const;

type ModelRow = ListModelsResponse['data'][number];

const COLUMNS: DataTableColumn<ModelRow>[] = [
  {
    key: 'model',
    header: 'Model',
    width: '28%',
    sortable: true,
    sortValue: (model) => model.displayName,
    render: (model) => (
      <div>
        <Link
          to={`/agents/libraries/models/${model.slug}`}
          className="text-[13px] font-semibold text-heading transition-colors hover:text-accent-text"
          onClick={(event) => event.stopPropagation()}
        >
          {model.displayName}
        </Link>
        <p className="mt-0.5 font-mono text-xs text-muted">{model.slug}</p>
      </div>
    ),
  },
  {
    key: 'provider',
    header: 'Provider',
    sortable: true,
    sortValue: (model) => model.provider,
    render: (model) => <span className="text-sm text-body">{model.provider}</span>,
  },
  {
    key: 'context',
    header: 'Context',
    sortable: true,
    sortValue: (model) => model.maxTokens,
    render: (model) => (
      <div className="text-sm text-body">
        {model.maxTokens?.toLocaleString()} context
        <p className="text-xs text-muted">{model.maxOutputTokens?.toLocaleString() ?? 'Unknown'} output</p>
      </div>
    ),
  },
  {
    key: 'capabilities',
    header: 'Capabilities',
    render: (model) => (
      <div className="flex max-w-[360px] flex-wrap gap-1">
        {CAPABILITY_KEYS.filter(([key]) => model[key]).map(([key, label]) => (
          <Chip key={key} tone="blue">
            {label}
          </Chip>
        ))}
      </div>
    ),
  },
  {
    key: 'diagnostics',
    header: 'Diagnostics',
    sortable: true,
    sortValue: (model) => model.findingCount,
    render: (model) =>
      model.findingCount > 0 ? (
        <Badge variant="warning" icon={<AlertTriangle size={12} aria-hidden="true" />}>
          {model.findingCount} {model.findingCount === 1 ? 'finding' : 'findings'}
        </Badge>
      ) : (
        <Badge variant="success" icon={<CheckCircle2 size={12} aria-hidden="true" />}>
          Clean
        </Badge>
      ),
  },
];

export const ModelCapabilitiesPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [providers, setProviders] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>(['enabled']);
  const query = useQuery(listModelsOptions());
  const models = query.data?.data ?? [];

  const providerCounts = new Map<string, number>();
  for (const model of models) providerCounts.set(model.provider, (providerCounts.get(model.provider) ?? 0) + 1);
  const providerOptions = [...providerCounts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([value, count]) => ({ value, label: value, count }));

  const statusOptions = [
    { value: 'enabled', label: 'Enabled', count: models.filter((model) => model.enabled).length },
    { value: 'disabled', label: 'Disabled', count: models.filter((model) => !model.enabled).length },
    { value: 'findings', label: 'With findings', count: models.filter((model) => model.findingCount > 0).length },
  ];

  const q = search.toLowerCase();
  const filtered = models.filter((model) => {
    if (q) {
      const haystack = `${model.slug} ${model.displayName} ${model.modelName} ${model.provider}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (providers.length > 0 && !providers.includes(model.provider)) return false;
    if (statuses.length > 0) {
      const modelStatuses = [model.enabled ? 'enabled' : 'disabled', model.findingCount > 0 ? 'findings' : 'clean'];
      if (!statuses.some((status) => modelStatuses.includes(status))) return false;
    }
    return true;
  });

  return (
    <ListPage
      title="Model capabilities"
      subtitle="Read-only catalogue used for runtime compatibility decisions."
      breadcrumbs={[{ label: 'Libraries', to: '/agents/libraries' }]}
      alert={
        query.isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load models"
            actions={
              <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          >
            Something went wrong fetching the model catalogue. Check your connection and try again.
          </Callout>
        ) : undefined
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search by provider, slug or name…" />}
          right={
            <>
              <MultiSelectDropdown
                label="Provider"
                options={providerOptions}
                values={providers}
                onChange={setProviders}
              />
              <MultiSelectDropdown label="Status" options={statusOptions} values={statuses} onChange={setStatuses} />
            </>
          }
        />
      }
    >
      {!query.isError && (
        <DataTable
          columns={COLUMNS}
          rows={filtered}
          rowKey={(model) => model.slug}
          onRowClick={(model) => navigate(`/agents/libraries/models/${model.slug}`)}
          loading={query.isLoading}
          pageSize={25}
          aria-label="Models"
          footer={
            filtered.length > 0 ? (
              <span>
                Showing {filtered.length} of {models.length} models
              </span>
            ) : undefined
          }
          empty={
            <EmptyState
              icon={BrainCircuit}
              title="No models found"
              description="No model capability rows match the current filters."
            />
          }
        />
      )}
    </ListPage>
  );
};
